import { ReactNode, useEffect } from 'react';

import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@componentLibrary/Dialog';
import { CloseIcon } from '@componentLibrary/Icon';
import IconButton from '@componentLibrary/IconButton';
import Typography from '@componentLibrary/Typography';
import { cn } from '@componentLibrary/utils';

import { SparkplugWhiteLogo } from '@components/logos';

export default ({
    isVisible,
    left,
    right,
    title,
    onClose,
    showHeader = true,
    showCloseButton = true,
    className = '',
}: {
    isVisible: boolean;
    left: ReactNode;
    right: ReactNode;
    title?: string;
    onClose: () => void;
    showHeader?: boolean;
    showCloseButton?: boolean;
    className?: string;
}) => {
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [onClose]);

    return (
        <Dialog
            open={isVisible}
            onOpenChange={(val) => {
                if (!val) {
                    onClose();
                }
            }}
        >
            <DialogContent
                variant="fullscreen"
                className={cn('h-full w-full flex !p-0 max-sm:flex-col gap-0', className)}
                showCloseButton={false}
            >
                <DialogTitle className="sr-only">{title || 'Fullscreen Modal'}</DialogTitle>
                <DialogDescription className="sr-only">
                    {title ? `${title} modal content` : 'Fullscreen modal content'}
                </DialogDescription>
                {showHeader ? (
                    <div className="h-full sm:w-1/2 max-sm:w-full flex flex-col gap-12 bg-cerulean-600 relative">
                        <div className="w-full h-16 min-h-16 p-2 pl-4 flex items-center gap-4 absolute top-0 left-0 ">
                            <SparkplugWhiteLogo height={28} />
                            {title && (
                                <>
                                    <span className="h-7 w-px bg-white" />
                                    <Typography variant="xl" className="text-white font-semibold">
                                        {title}
                                    </Typography>
                                </>
                            )}
                            <div
                                className="sm:hidden ml-auto"
                                data-testid="fullscreen-modal-close-button-mobile"
                            >
                                <IconButton
                                    aria-label="close"
                                    onClick={onClose}
                                    className="!bg-gray-50 hover:!bg-gray-100 !text-gray-900"
                                >
                                    <CloseIcon />
                                </IconButton>
                            </div>
                        </div>
                        {left}
                    </div>
                ) : (
                    left
                )}
                <div className="h-full sm:w-1/2 max-sm:w-full flex flex-col gap-12 relative">
                    {showCloseButton && (
                        <div
                            className="max-sm:hidden flex justify-end p-3 absolute top-0 right-0"
                            data-testid="fullscreen-modal-close-button"
                        >
                            <IconButton
                                aria-label="close"
                                onClick={onClose}
                                className="!bg-gray-50 hover:!bg-gray-100 !text-gray-900"
                            >
                                <CloseIcon />
                            </IconButton>
                        </div>
                    )}
                    {right}
                </div>
            </DialogContent>
        </Dialog>
    );
};
